import { lazy } from 'react';
import { IconShieldLock, IconKey, IconServer } from '@tabler/icons-react';
import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import { isAdmin } from '@ayocore/platform/identity/client/guards';

/**
 * Бутстрап клиентской части OAuth-модуля.
 *
 * Публичные маршруты (без аутентификации):
 *   /oauth/login           — форма входа для Authorization Code flow
 *   /oauth-invite          — установка пароля по инвайту
 *   /oauth-reset-password  — сброс пароля
 *
 * Административные маршруты (требуют аутентификацию):
 *   /oauth/tenants              — список тенантов
 *   /oauth/tenants/:id/users    — пользователи тенанта
 *   /oauth/clients              — список OAuth-клиентов
 *   /oauth/clients/:id          — просмотр OAuth-клиента
 */
export async function setup(app: AppRuntime): Promise<void> {
  const oauth = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
  });

  // Группа навигации OAuth
  const groupOAuth = oauth.createNavigationGroup({
    key: 'oauth',
    label: 'OAuth',
    icon: IconShieldLock,
    order: 1,
  });

  // Публичные маршруты (OAuth flow)
  oauth.usePublicRoute({
    path: '/oauth/login',
    component: lazy(() => import('./components/OAuthLoginPage')),
  });

  oauth.usePublicRoute({
    path: '/oauth-invite',
    component: lazy(() => import('./components/InvitePage')),
  });

  oauth.usePublicRoute({
    path: '/oauth-reset-password',
    component: lazy(() => import('./components/ResetPasswordPage')),
  });

  // Административные маршруты — группа OAuth видна только администраторам.
  // Если guard скроет все пункты группы, сама группа в меню тоже не отрисуется.
  oauth.useNavigation([
    {
      group: groupOAuth,
      icon: IconServer,
      label: 'Тенанты',
      url: '/oauth/tenants',
      component: lazy(() => import('./components/TenantsList')),
      guard: isAdmin,
    },
    {
      group: groupOAuth,
      icon: IconShieldLock,
      label: 'Пользователи тенанта',
      url: '/oauth/tenants/:id/users',
      component: lazy(() => import('./components/TenantUsersPage')),
      hiddenFromMenu: true,
    },
    {
      group: groupOAuth,
      icon: IconKey,
      label: 'Клиенты',
      url: '/oauth/clients',
      component: lazy(() => import('./components/OAuthClientsList')),
      guard: isAdmin,
    },
    {
      group: groupOAuth,
      icon: IconKey,
      label: 'Просмотр клиента',
      url: '/oauth/clients/:id',
      component: lazy(() =>
        import('./components/OAuthClientViewPage').then(m => ({ default: m.OAuthClientViewPage }))
      ),
      hiddenFromMenu: true,
    },
  ]);
}
